import { Slot } from '@radix-ui/react-slot';
import {
  useCallback,
  type ComponentPropsWithRef,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';

import { EVENTKEY_MAP } from '@repo/lib/constants';

import { useTooltip } from './TooltipRoot.js';

export type TooltipTriggerProps = ComponentPropsWithRef<'span'> & {
  asChild?: boolean;
  children: ReactNode;
};

export const TooltipTrigger = (props: TooltipTriggerProps) => {
  const {
    asChild,
    children,
    onBlur,
    onFocus,
    onKeyDown,
    onMouseEnter,
    onMouseLeave,
    onPointerDown,
    ...attrs
  } = props;
  const { close, id, open, triggerRef } = useTooltip();

  const handleMouseEnter = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      onMouseEnter?.(e);
      open();
    },
    [onMouseEnter, open],
  );

  const handleMouseLeave = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      onMouseLeave?.(e);
      close();
    },
    [onMouseLeave, close],
  );

  const handleFocus = useCallback(
    (e: FocusEvent<HTMLSpanElement>) => {
      onFocus?.(e);
      if (e.target.matches(':focus-visible')) {
        open({ instant: true });
      }
    },
    [onFocus, open],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLSpanElement>) => {
      onBlur?.(e);
      close();
    },
    [onBlur, close],
  );

  const handlePointerDown = useCallback(
    (e: PointerEvent<HTMLSpanElement>) => {
      onPointerDown?.(e);
      close({ instant: true });
    },
    [onPointerDown, close],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLSpanElement>) => {
      onKeyDown?.(e);
      if (e.key === EVENTKEY_MAP.ENTER || e.key === EVENTKEY_MAP.SPACE) {
        close({ instant: true });
      }
    },
    [onKeyDown, close],
  );

  const Component = asChild ? Slot : 'span';

  return (
    <Component
      aria-describedby={id}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onPointerDown={handlePointerDown}
      ref={triggerRef}
      tabIndex={asChild ? undefined : 0}
      {...attrs}
    >
      {children}
    </Component>
  );
};
